import React, {useState} from "react";
import {Table, Space, Popconfirm, Tooltip, Popover, Button, Tag, Input} from "antd";
import {EditOutlined, DeleteOutlined, CloseOutlined, StopOutlined} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import {EmptyMessage} from "components/_defaultComponents/EmptyMessage/EmptyMessage";
import {Link} from "react-router-dom";
import {toTurkishProperCase} from "utils/toTurkishProperCase";
import {useStore} from "hooks/useStore";
import {Shortener} from "components/_defaultComponents/Shortener/Shortener";
import {SubCompanyPaymentContractsTable} from "components/_defaultComponents/SubCompanyPaymentContractsTable/SubCompanyPaymentContractsTable";
import moment from "moment";
import {RankIcon} from "components/_defaultComponents/RankIcon/RankIcon";
import {
    courierRequestPriorityRankIconKeys,
    courierRequestStatusEnums,
    courierRequestStatuses
} from "constants/constants";
import {generatePaymentContractActivityStatus} from "utils/generatePaymentContractActivityStatus";
import {CourierRequestCouriersModal} from "./CourierRequestCouriersModal";

const MIN_CLOSE_REASON_LENGTH = 15;

// Aksiyon sütunundaki ikon butonlar için renkli arkaplan (Jira örnek tasarımdaki gibi)
const actionButtonColors = {
    edit: "#1890ff",
    delete: "#ff4d4f",
    close: "#faad14",
    rejectedCouriers: "#722ed1"
};

const actionButtonStyle = color => ({
    background: color,
    borderColor: color,
    color: "#fff"
});

export const CourierRequestsTable = ({
    courierRequestLoading,
    courierRequestData,
    handleEditCourierRequest,
    handleDeleteCourierRequest,
    handleCloseCourierRequest,
    closeReason,
    setCloseReason,
    pagination,
    setPagination
}) => {
    const stores = useStore();
    const {t} = useTranslation(["common"]);

    // Sayıya tıklanınca açılan, ilgili kategorinin kurye detaylarını gösteren modal
    const [couriersModalState, setCouriersModalState] = useState({
        visible: false,
        courierRequestId: null,
        type: null
    });

    const openCouriersModal = (courierRequestId, type) => {
        setCouriersModalState({visible: true, courierRequestId, type});
    };

    const closeCouriersModal = () => {
        setCouriersModalState({visible: false, courierRequestId: null, type: null});
    };

    const renderCourierCountTag = (count, courierRequestId, type) =>
        count > 0 ? (
            <Tag
                style={{cursor: "pointer"}}
                onClick={() => openCouriersModal(courierRequestId, type)}
            >
                {count}
            </Tag>
        ) : (
            <Tag> {count} </Tag>
        );

    // Table pagination change handler
    const handleTableChange = _pagination => {
        if (_pagination.pageSize !== pagination.limit) {
            _pagination.current = 1;
        }
        setPagination({
            limit: _pagination.pageSize,
            skip: (_pagination.current - 1) * _pagination.pageSize
        });
    };

    return (
        <>
            <Table
                scroll={{x: "max-content"}}
                size="small"
                rowKey="_id"
                loading={courierRequestLoading}
                dataSource={Array.isArray(courierRequestData?.data) ? courierRequestData.data : []}
                locale={{
                    emptyText: <EmptyMessage />
                }}
                onChange={handleTableChange}
                pagination={{
                    hideOnSinglePage: true,
                    current: pagination.skip / pagination.limit + 1,
                    pageSize: pagination.limit,
                    total: courierRequestData?.dataCount || 0,
                    showSizeChanger: false,
                    showQuickJumper: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}`,
                    pageSizeOptions: ["10", "20", "50", "100"]
                }}
            >
                <Table.Column
                    dataIndex={["createdAt"]}
                    title="Tarih"
                    ellipsis={{
                        tooltip: true
                    }}
                    width={100}
                    render={createdAt => (
                        <Tooltip title={moment(createdAt).format("DD.MM.YYYY HH:mm:ss")}>
                            {moment(createdAt).format("DD.MM.YYYY")}
                        </Tooltip>
                    )}
                />
                <Table.Column
                    dataIndex={["priority"]}
                    title="Öncelik"
                    ellipsis={{
                        tooltip: true
                    }}
                    width={50}
                    align="center"
                    render={(priority, row) => (
                        <Tooltip title={row?.priorityText}>
                            <span>
                                <RankIcon
                                    style={{fontSize: "24px"}}
                                    rankType={courierRequestPriorityRankIconKeys?.[priority]}
                                />
                            </span>
                        </Tooltip>
                    )}
                />
                <Table.Column
                    dataIndex={["statusText"]}
                    title="Durum"
                    ellipsis={{
                        tooltip: true
                    }}
                    width={50}
                    render={(statusText, row) => (
                        <Tag
                            color={
                                courierRequestStatuses.find(status => status.value === row?.status)
                                    ?.color
                            }
                        >
                            <Shortener text={statusText || "-"} length={32} />
                        </Tag>
                    )}
                />
                <Table.Column
                    dataIndex={["name"]}
                    title="İsim"
                    width={150}
                    render={name => <Shortener text={name} length={32} />}
                />
                <Table.Column
                    dataIndex={["zone", "name"]}
                    title="Bölge"
                    ellipsis={{
                        tooltip: true
                    }}
                    width={50}
                    render={zoneName => <Shortener text={zoneName} length={32} />}
                />

                <Table.Column
                    dataIndex={["zoneManagement", "name"]}
                    title="Bölge Müdürlüğü"
                    ellipsis={{
                        tooltip: true
                    }}
                    width={120}
                    render={(zoneManagementName, row) => (
                        <>
                            <Shortener
                                text={zoneManagementName}
                                length={12}
                                render={text => <span>{text}</span>}
                            />
                            <br />
                            BM :{" "}
                            <Shortener
                                text={row?.zoneManagementUser?.fullName || "-"}
                                length={12}
                            />
                        </>
                    )}
                />
                <Table.Column
                    dataIndex="cityName"
                    title="Şehir / İlçe"
                    ellipsis={{
                        tooltip: true
                    }}
                    width={80}
                    render={(cityName, row) => (
                        <>
                            <Shortener text={toTurkishProperCase(cityName) || "-"} length={32} />
                            <br />
                            <Shortener
                                text={toTurkishProperCase(row?.districtName) || "-"}
                                length={12}
                            />
                        </>
                    )}
                />

                <Table.Column
                    dataIndex={["company", "companyName"]}
                    title="Firma / Alt Firma"
                    ellipsis={{
                        tooltip: true
                    }}
                    width={50}
                    render={(companyName, row) => (
                        <>
                            <Shortener
                                text={companyName || "-"}
                                length={12}
                                render={text => (
                                    <Link to={`/companies/${row?.company?._id}`} target="_blank">
                                        {text}
                                    </Link>
                                )}
                            />
                            <br />
                            <Shortener
                                text={row?.subCompany?.name || "-"}
                                length={12}
                                render={text => (
                                    <Link
                                        to={`/companies/sub-company/${row?.subCompany?._id}`}
                                        target="_blank"
                                    >
                                        {text}
                                    </Link>
                                )}
                            />
                        </>
                    )}
                />
                <Table.Column
                    dataIndex={["workingModel", "label", "tr"]}
                    title="Çalışma Modeli"
                    ellipsis={{
                        tooltip: true
                    }}
                    width={50}
                    render={workingModelName => (
                        <Shortener text={workingModelName || "-"} length={12} />
                    )}
                />
                <Table.Column
                    dataIndex={["requestedCourierCount"]}
                    title={
                        <>
                            Kurye <br /> Talep Sayısı
                        </>
                    }
                    ellipsis={{
                        tooltip: true
                    }}
                    width={50}
                    render={count => count}
                />
                <Table.Column
                    dataIndex={["applicantCourierCount"]}
                    title={
                        <>
                            Başvuran <br /> Kurye Sayısı
                        </>
                    }
                    ellipsis={{
                        tooltip: true
                    }}
                    width={50}
                    align="center"
                    render={(count, record) =>
                        renderCourierCountTag(count, record._id, "applicant")
                    }
                />
                <Table.Column
                    dataIndex={["missingDocumentCourierCount"]}
                    title={
                        <>
                            Eksik Belge <br /> Kurye Sayısı
                        </>
                    }
                    ellipsis={{
                        tooltip: true
                    }}
                    width={50}
                    align="center"
                    render={(count, record) =>
                        renderCourierCountTag(count, record._id, "missingDocument")
                    }
                />
                <Table.Column
                    dataIndex={["approvedCourierCount"]}
                    title={
                        <>
                            Onaylanan <br /> Kurye Sayısı
                        </>
                    }
                    ellipsis={{
                        tooltip: true
                    }}
                    width={50}
                    align="center"
                    render={(count, record) => renderCourierCountTag(count, record._id, "approved")}
                />
                <Table.Column
                    dataIndex={["rejectedCourierCount"]}
                    title={
                        <>
                            Olumsuz <br /> Kurye Sayısı
                        </>
                    }
                    ellipsis={{
                        tooltip: true
                    }}
                    width={50}
                    align="center"
                    render={(count, record) => renderCourierCountTag(count, record._id, "rejected")}
                />
                <Table.Column
                    dataIndex={["calledCourierCount"]}
                    title={
                        <>
                            Aranan <br /> Kurye Sayısı
                        </>
                    }
                    ellipsis={{
                        tooltip: true
                    }}
                    width={50}
                    align="center"
                    render={(count, record) => renderCourierCountTag(count, record._id, "called")}
                />
                <Table.Column
                    dataIndex={["reachedCourierCount"]}
                    title={
                        <>
                            Ulaşılan <br /> Kurye Sayısı
                        </>
                    }
                    ellipsis={{
                        tooltip: true
                    }}
                    width={50}
                    align="center"
                    render={(count, record) => renderCourierCountTag(count, record._id, "reached")}
                />
                <Table.Column
                    dataIndex={["activeSubcompanyPaymentContract"]}
                    title="Sözleşme"
                    ellipsis={{
                        tooltip: true
                    }}
                    width={50}
                    render={activeSubcompanyPaymentContract =>
                        activeSubcompanyPaymentContract ? (
                            <Popover
                                zIndex={999}
                                trigger="click"
                                content={
                                    <SubCompanyPaymentContractsTable
                                        subCompanyPaymentContracts={
                                            [
                                                {
                                                    ...activeSubcompanyPaymentContract,
                                                    activityStatus: generatePaymentContractActivityStatus(
                                                        activeSubcompanyPaymentContract
                                                    )
                                                }
                                            ] || []
                                        }
                                        size="small"
                                    />
                                }
                            >
                                <Button size="small" type="link">
                                    Görüntüle
                                </Button>
                            </Popover>
                        ) : (
                            <span>-</span>
                        )
                    }
                />
                <Table.Column
                    dataIndex={["note"]}
                    title="Not"
                    ellipsis={{
                        tooltip: true
                    }}
                    width={50}
                    render={note => <Shortener text={note || "-"} length={12} />}
                />

                <Table.Column
                    align="right"
                    title={
                        <div style={{textAlign: "center", width: "100%"}}>
                            {t("actions.action", {ns: "common"})}
                        </div>
                    }
                    render={item => (
                        <Space direction="horizontal" wrap>
                            {stores.meStore.checkRole("createCourierRequest") &&
                                stores.meStore.checkPermission("courierRequests.requestActions") &&
                                ![
                                    courierRequestStatusEnums.COMPLETED,
                                    courierRequestStatusEnums.CLOSED
                                ].includes(item?.status) && (
                                    <Tooltip title="Düzenle">
                                        <Button
                                            shape="circle"
                                            size="small"
                                            icon={<EditOutlined />}
                                            style={actionButtonStyle(actionButtonColors.edit)}
                                            onClick={() => handleEditCourierRequest(item)}
                                        />
                                    </Tooltip>
                                )}
                            {stores.meStore.checkRole("deleteCourierRequest") &&
                                ![
                                    courierRequestStatusEnums.COMPLETED,
                                    courierRequestStatusEnums.CLOSED
                                ].includes(item?.status) && (
                                    <Popconfirm
                                        title="Bu kurye talebini silmek istediğinizden emin misiniz?"
                                        onConfirm={() => handleDeleteCourierRequest(item)}
                                        okText="Evet"
                                        cancelText="Hayır"
                                    >
                                        <Tooltip title="Sil">
                                            <Button
                                                shape="circle"
                                                size="small"
                                                danger
                                                icon={<DeleteOutlined />}
                                            />
                                        </Tooltip>
                                    </Popconfirm>
                                )}
                            {stores.meStore.checkRole("closeCourierRequest") &&
                                stores.meStore.checkPermission("courierRequests.requestActions") &&
                                ![
                                    courierRequestStatusEnums.COMPLETED,
                                    courierRequestStatusEnums.CLOSED
                                ].includes(item?.status) && (
                                    <Popconfirm
                                        icon={null}
                                        title={
                                            <>
                                                <h3>
                                                    Bu kurye talebini kapatmak istediğinizden emin
                                                    misiniz?
                                                </h3>
                                                <br />
                                                Lütfen, kurye talebi kapatılma nedenini giriniz. (En
                                                az {MIN_CLOSE_REASON_LENGTH} karakterlik açıklama
                                                girmelisiniz.)
                                                <br />
                                                <br />
                                                <Input.TextArea
                                                    placeholder="Kurye talebi kapatılma nedenini giriniz."
                                                    value={closeReason}
                                                    onChange={e => setCloseReason(e.target.value)}
                                                    rows={4}
                                                />
                                            </>
                                        }
                                        onConfirm={() =>
                                            handleCloseCourierRequest({
                                                record: item,
                                                reason: closeReason
                                            })
                                        }
                                        okText={`Evet ${
                                            closeReason?.trim()?.length < MIN_CLOSE_REASON_LENGTH
                                                ? `(${
                                                      closeReason?.trim()?.length
                                                  }/${MIN_CLOSE_REASON_LENGTH})`
                                                : ""
                                        }`}
                                        cancelText="Hayır"
                                        okButtonProps={{
                                            disabled:
                                                closeReason?.trim()?.length <
                                                MIN_CLOSE_REASON_LENGTH
                                        }}
                                    >
                                        <Tooltip title="Kapat">
                                            <Button
                                                shape="circle"
                                                size="small"
                                                icon={<CloseOutlined />}
                                                style={actionButtonStyle(actionButtonColors.close)}
                                            />
                                        </Tooltip>
                                    </Popconfirm>
                                )}
                            <Tooltip title="Olumsuz Kuryeler">
                                <Button
                                    shape="circle"
                                    size="small"
                                    icon={<StopOutlined />}
                                    style={actionButtonStyle(actionButtonColors.rejectedCouriers)}
                                    onClick={() =>
                                        window.open(
                                            `/couriers?activationStep=rejected&zoneId=${item?.zoneId}`,
                                            "_blank"
                                        )
                                    }
                                />
                            </Tooltip>
                        </Space>
                    )}
                />
            </Table>
            <CourierRequestCouriersModal
                visibility={couriersModalState.visible}
                courierRequestId={couriersModalState.courierRequestId}
                type={couriersModalState.type}
                onCancel={closeCouriersModal}
            />
        </>
    );
};
